import { useCallback, useEffect, useRef, useState } from 'react'
import { UNITY_AUDIO } from '../content/audio'
import type { Phase } from '../shared/types'

type TrackName = 'menu' | 'visit' | 'result'

export interface MuseumAudioController {
  muted: boolean
  needsGesture: boolean
  activate: () => void
  toggle: () => void
  setVideoPlaying: (playing: boolean) => void
}

export function useMuseumAudio(phase: Phase): MuseumAudioController {
  const tracksRef = useRef<Record<TrackName, HTMLAudioElement> | null>(null)
  const openingRef = useRef<HTMLAudioElement | null>(null)
  const phaseRef = useRef(phase)
  const activatedRef = useRef(false)
  const mutedRef = useRef(false)
  const focusedRef = useRef(typeof document === 'undefined' || (!document.hidden && (typeof document.hasFocus !== 'function' || document.hasFocus())))
  const pausedForBackgroundRef = useRef(false)
  const videoPlayingRef = useRef(false)
  const playbackRevisionRef = useRef(0)
  const openingPendingRef = useRef(false)
  const openingDoneRef = useRef(false)
  const openingSessionRef = useRef(0)
  const unmountPauseTimerRef = useRef<number | undefined>(undefined)
  const [muted, setMuted] = useState(false)
  const [needsGesture, setNeedsGesture] = useState(false)
  const [audioReady, setAudioReady] = useState(false)

  const desiredTrack = useCallback((): TrackName | null => {
    const currentPhase = phaseRef.current
    if (currentPhase === 'welcome') return 'menu'
    if (currentPhase === 'guide' || currentPhase === 'exploring' || currentPhase === 'paused') return 'visit'
    if (currentPhase === 'archive') return 'result'
    return null
  }, [])

  const pauseAll = useCallback(() => {
    playbackRevisionRef.current += 1
    for (const track of Object.values(tracksRef.current ?? {})) track.pause()
    openingRef.current?.pause()
  }, [])

  const applyVolumes = useCallback(() => {
    const duck = videoPlayingRef.current ? 0.25 : 1
    for (const name of ['menu', 'visit', 'result'] as const) {
      const track = tracksRef.current?.[name]
      if (track) track.volume = mutedRef.current ? 0 : UNITY_AUDIO[name].volume * duck
    }
    if (openingRef.current) openingRef.current.volume = mutedRef.current ? 0 : UNITY_AUDIO.opening.volume
  }, [])

  const playSelected = useCallback(() => {
    const tracks = tracksRef.current
    if (!tracks) return
    if (mutedRef.current || !focusedRef.current || pausedForBackgroundRef.current || phaseRef.current === 'paused') {
      pauseAll()
      return
    }

    const selected = desiredTrack()
    if (!selected) {
      pauseAll()
      return
    }
    const revision = ++playbackRevisionRef.current
    applyVolumes()
    for (const [name, track] of Object.entries(tracks) as [TrackName, HTMLAudioElement][]) {
      if (name !== selected) track.pause()
    }
    if (selected !== 'menu') openingRef.current?.pause()

    const reportFailure = (error: unknown) => {
      if (revision !== playbackRevisionRef.current) return
      if (error instanceof DOMException && error.name === 'AbortError') return
      setNeedsGesture(true)
    }
    void tracks[selected].play().then(() => {
      if (revision === playbackRevisionRef.current) {
        activatedRef.current = true
        setAudioReady(true)
      }
    }).catch(reportFailure)

    if (selected === 'menu' && !openingDoneRef.current && !openingPendingRef.current) {
      const opening = openingRef.current
      if (!opening) return
      const session = openingSessionRef.current
      openingPendingRef.current = true
      void opening.play().then(() => {
        if (session === openingSessionRef.current) openingPendingRef.current = false
      }).catch((error: unknown) => {
        if (session !== openingSessionRef.current) return
        openingPendingRef.current = false
        reportFailure(error)
      })
    }
  }, [applyVolumes, desiredTrack, pauseAll])

  const activate = useCallback(() => {
    activatedRef.current = true
    focusedRef.current = !document.hidden
    pausedForBackgroundRef.current = false
    if (!mutedRef.current) setNeedsGesture(false)
    applyVolumes()
    // Call play in the input event itself so browsers can grant audio activation.
    playSelected()
  }, [applyVolumes, playSelected])

  const toggle = useCallback(() => {
    if (mutedRef.current) {
      mutedRef.current = false
      setMuted(false)
      activate()
      return
    }
    if (needsGesture || !audioReady || pausedForBackgroundRef.current || !activatedRef.current) {
      activate()
      return
    }
    mutedRef.current = true
    setMuted(true)
    setNeedsGesture(false)
    pauseAll()
  }, [activate, audioReady, needsGesture, pauseAll])

  const setVideoPlaying = useCallback((playing: boolean) => {
    videoPlayingRef.current = playing
    applyVolumes()
  }, [applyVolumes])

  useEffect(() => {
    if (typeof Audio === 'undefined' || tracksRef.current) return
    tracksRef.current = {
      menu: new Audio(UNITY_AUDIO.menu.src),
      visit: new Audio(UNITY_AUDIO.visit.src),
      result: new Audio(UNITY_AUDIO.result.src),
    }
    for (const name of ['menu', 'visit', 'result'] as const) {
      const track = tracksRef.current[name]
      track.loop = true
      track.preload = 'auto'
      track.volume = UNITY_AUDIO[name].volume
    }
    const opening = new Audio(UNITY_AUDIO.opening.src)
    opening.preload = 'auto'
    opening.volume = UNITY_AUDIO.opening.volume
    opening.onended = () => {
      if (!opening.ended) return
      openingDoneRef.current = true
      openingPendingRef.current = false
    }
    openingRef.current = opening
  }, [])

  useEffect(() => {
    const previousTrack = phaseRef.current === 'welcome' ? 'menu' : phaseRef.current === 'guide' || phaseRef.current === 'exploring' || phaseRef.current === 'paused' ? 'visit' : phaseRef.current === 'archive' ? 'result' : null
    const enteringMenu = phaseRef.current !== 'welcome' && phase === 'welcome'
    const changed = phaseRef.current !== phase
    phaseRef.current = phase
    const nextTrack = desiredTrack()
    if (changed && previousTrack !== nextTrack && nextTrack) {
      const track = tracksRef.current?.[nextTrack]
      if (track) track.currentTime = 0
    }
    if (changed && enteringMenu) {
      openingSessionRef.current += 1
      openingDoneRef.current = false
      openingPendingRef.current = false
      const opening = openingRef.current
      if (opening) {
        opening.currentTime = 0
      }
    }
    if (changed && phase === 'exploring') pausedForBackgroundRef.current = false
    if (activatedRef.current) playSelected()
    else pauseAll()
  }, [phase, desiredTrack, playSelected, pauseAll])

  useEffect(() => {
    const pauseForBackground = () => {
      focusedRef.current = false
      pausedForBackgroundRef.current = true
      pauseAll()
      setNeedsGesture(true)
    }
    const onFocus = () => { focusedRef.current = true }
    const onVisibility = () => {
      if (document.hidden) pauseForBackground()
      else focusedRef.current = true
    }
    window.addEventListener('blur', pauseForBackground)
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.removeEventListener('blur', pauseForBackground)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [pauseAll])

  useEffect(() => {
    if (!mutedRef.current) return
    applyVolumes()
  }, [muted, applyVolumes])

  useEffect(() => {
    if (unmountPauseTimerRef.current !== undefined) window.clearTimeout(unmountPauseTimerRef.current)
    unmountPauseTimerRef.current = undefined
    return () => {
      // StrictMode replays effect cleanup immediately during development. Defer
      // disposal so the following setup can cancel it without interrupting audio.
      unmountPauseTimerRef.current = window.setTimeout(() => {
        pauseAll()
        unmountPauseTimerRef.current = undefined
      }, 0)
    }
  }, [pauseAll])

  return { muted, needsGesture: needsGesture || !audioReady || pausedForBackgroundRef.current, activate, toggle, setVideoPlaying }
}
